<template>
  <a-drawer width="35%" :visible="open" @close="onClose">
    <a-divider orientation="left">
      <b>{{ formTitle }}</b>
    </a-divider>
    <a-form-model ref="form" :model="form" :rules="formRules" :label-col="{ span: 6 }" :wrapper-col="{ span: 16 }">
      <a-form-model-item label="Account Type" prop="type">
        <a-select v-model="form.type" placeholder="Please select account type" @change="handleTypeChange">
          <a-select-option :value="0">Mobile</a-select-option>
          <a-select-option :value="1">Email</a-select-option>
        </a-select>
      </a-form-model-item>

      <a-form-model-item v-if="form.type === 0" label="Mobile" prop="mobile">
        <a-input v-model="form.mobile" placeholder="Please enter mobile number" allow-clear />
      </a-form-model-item>
      <a-form-model-item v-if="form.type === 0" label="Mobile Code" prop="mobileCode">
        <a-select
          v-model="form.mobileCode"
          placeholder="Please select mobile code"
          :loading="mobileCodeLoading"
          :filter-option="false"
          show-search
          allow-clear
          auto-clear-search-value
          @search="onMobileCodeSearch"
          @dropdownVisibleChange="onMobileCodeDropdownVisible"
        >
          <a-select-option
            v-for="(item, idx) in filteredMobileCodeOptions"
            :key="'mc-' + item.key + '-' + idx"
            :value="item.value"
          >
            {{ item.label }}
          </a-select-option>
        </a-select>
      </a-form-model-item>

      <a-form-model-item v-if="form.type === 1" label="Email" prop="email">
        <a-input v-model="form.email" placeholder="Please enter email" allow-clear />
      </a-form-model-item>

      <a-form-model-item label="First Name" prop="firstName">
        <a-input v-model="form.firstName" placeholder="Please enter first name" allow-clear />
      </a-form-model-item>
      <a-form-model-item label="Last Name" prop="lastName">
        <a-input v-model="form.lastName" placeholder="Please enter last name" allow-clear />
      </a-form-model-item>
      <a-form-model-item label="User Code" prop="userCode">
        <a-input v-model="form.userCode" placeholder="Please enter user code" allow-clear />
      </a-form-model-item>

      <div class="bottom-control">
        <a-space>
          <a-button type="primary" :loading="submitLoading" @click="submitForm">
            Save
          </a-button>
          <a-button type="dashed" @click="cancel">
            Cancel
          </a-button>
        </a-space>
      </div>
    </a-form-model>
  </a-drawer>
</template>

<script>
import { addUser } from '@/api/biz/user'
import { listMobileCode } from '@/api/mobileCode/mobileCode'

export default {
  name: 'AddUserForm',
  data () {
    return {
      open: false,
      submitLoading: false,
      formTitle: 'Add User',
      form: {
        type: 0,
        mobileCode: undefined,
        mobile: undefined,
        email: undefined,
        firstName: undefined,
        lastName: undefined,
        userCode: null
      },
      mobileCodeLoading: false,
      mobileCodeOptions: [],
      /** 搜索关键字，配合 computed 过滤，避免自定义 filterOption 依赖 Option VNode 导致无法选中/无法清除 */
      mobileCodeSearch: ''
    }
  },
  computed: {
    /** 按账户类型校验：Email 模式下不要求 mobile/mobileCode，避免 async-validator 反复报 mobileCode is required */
    formRules () {
      const base = {
        type: [
          { required: true, message: 'Account type is required', trigger: 'change' }
        ],
        firstName: [
          { required: true, message: 'First name is required', trigger: 'blur' }
        ],
        lastName: [
          { required: true, message: 'Last name is required', trigger: 'blur' }
        ]
      }
      if (this.form.type === 0) {
        return {
          ...base,
          mobile: [
            { required: true, message: 'Mobile number is required', trigger: 'blur' }
          ],
          mobileCode: [
            {
              validator: (rule, value, callback) => {
                if (value === undefined || value === null || value === '') {
                  callback(new Error('Mobile code is required'))
                } else {
                  callback()
                }
              },
              trigger: 'change'
            }
          ]
        }
      }
      return {
        ...base,
        email: [
          { required: true, message: 'Email is required', trigger: 'blur' },
          { type: 'email', message: 'Invalid email format', trigger: 'blur' }
        ]
      }
    },
    filteredMobileCodeOptions () {
      const list = this.dedupeMobileCodeRowsByDigits(this.sanitizeMobileCodeOptionRows(this.mobileCodeOptions))
      const raw = (this.mobileCodeSearch || '').trim()
      let base
      if (!raw) {
        base = list
      } else {
        const digitQuery = raw.replace(/^\++/, '').trim()
        if (/^\d+$/.test(digitQuery)) {
          base = list.filter((item) => {
            const codeDigits = this.normalizeAreaCodeDigits(item.value)
            return codeDigits && codeDigits.startsWith(digitQuery)
          })
        } else {
          const q = raw.toLowerCase()
          base = list.filter(item => String(item.label).toLowerCase().includes(q))
        }
      }
      base = this.dedupeMobileCodeRowsByDigits(base)
      const sel = this.form.mobileCode
      if (sel === undefined || sel === null || sel === '') return base
      const selDigits = this.normalizeAreaCodeDigits(sel)
      if (!selDigits) return base
      const selItem = list.find(o => this.normalizeAreaCodeDigits(o.value) === selDigits)
      if (!selItem) return base
      const alreadyInBase = base.some(o => this.normalizeAreaCodeDigits(o.value) === selDigits)
      if (alreadyInBase) return base
      return this.dedupeMobileCodeRowsByDigits([selItem, ...base])
    }
  },
  methods: {
    onClose () {
      this.cancel()
    },
    handleAdd () {
      this.reset()
      this.loadMobileCodeOptions()
      this.open = true
    },
    onMobileCodeSearch (value) {
      this.mobileCodeSearch = value
    },
    onMobileCodeDropdownVisible (open) {
      if (!open) {
        this.mobileCodeSearch = ''
      }
    },
    /** 区号统一成连续数字再比前缀，兼容接口返回 1、"1"、"+1"、"+19" 等 */
    normalizeAreaCodeDigits (areaCode) {
      return String(areaCode == null ? '' : areaCode).replace(/\D/g, '')
    },
    /** 去掉空项、保证 key，避免 v-for 出现 undefined 导致 sameVnode 读 key 报错 */
    sanitizeMobileCodeOptionRows (rows) {
      if (!Array.isArray(rows)) return []
      return rows.filter((row) => {
        if (!row || row.key === undefined || row.key === null) return false
        if (row.value === undefined || row.value === null) return false
        return true
      })
    },
    /**
     * 同一区号只能有一条 Option：Ant Design Vue 会用 option 的 value 做内部 key，
     * 多条 areaCode 相同（如多个 '+1'）会导致 Duplicate keys、无法选中。
     */
    dedupeMobileCodeRowsByDigits (rows) {
      const seen = new Set()
      const out = []
      for (const row of rows) {
        if (!row || row.key === undefined || row.key === null) continue
        if (row.value === undefined || row.value === null) continue
        const digits = this.normalizeAreaCodeDigits(row.value)
        const dedupeKey = digits || `raw:${String(row.value)}`
        if (seen.has(dedupeKey)) continue
        seen.add(dedupeKey)
        out.push(row)
      }
      return out
    },
    handleTypeChange (value) {
      if (value === 0) {
        this.form.email = undefined
      } else {
        this.form.mobile = undefined
        this.form.mobileCode = undefined
      }
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.clearValidate(['mobile', 'mobileCode', 'email'])
        }
      })
    },
    cancel () {
      this.open = false
      this.reset()
    },
    reset () {
      this.mobileCodeSearch = ''
      this.form = {
        type: 0,
        mobileCode: undefined,
        mobile: undefined,
        email: undefined,
        firstName: undefined,
        lastName: undefined,
        userCode: null
      }
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.resetFields()
        }
      })
    },
    submitForm () {
      this.$refs.form.validate(valid => {
        if (!valid) {
          return false
        }

        const payload = {
          type: this.form.type,
          firstName: this.form.firstName,
          lastName: this.form.lastName,
          userCode: this.form.userCode
        }
        if (this.form.type === 0) {
          payload.mobile = this.form.mobile
          payload.mobileCode = this.form.mobileCode
        } else {
          payload.email = this.form.email
        }

        this.submitLoading = true
        addUser(payload).then(() => {
          this.$message.success('Added successfully')
          this.open = false
          this.$emit('ok')
          this.reset()
        }).finally(() => {
          this.submitLoading = false
        })
      })
    },
    loadMobileCodeOptions () {
      this.mobileCodeLoading = true
      listMobileCode().then((res) => {
        const rows = Array.isArray(res.data?.records) ? res.data.records : (Array.isArray(res.data) ? res.data : [])
        const mapped = rows
          .filter(item => item && item.areaCode !== undefined && item.areaCode !== null)
          .map((item, index) => ({
            key: `mc-${index}-${String(item.areaCode)}`,
            label: `${item.country || ''} (${item.areaCode})`,
            value: item.areaCode
          }))
        this.mobileCodeOptions = this.dedupeMobileCodeRowsByDigits(mapped)
      }).finally(() => {
        this.mobileCodeLoading = false
      })
    }
  }
}
</script>

